import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const ROW_HEIGHT = 30;
const MARGIN = { top: 40, right: 60, bottom: 40, left: 60 };
const CENTER_COL_WIDTH = 50;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#374151',
  },
  legendDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
  },
  legendDiamond: {
    width: '10px',
    height: '10px',
    transform: 'rotate(45deg)',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function StatePricePyramid({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return [...data].sort((a, b) => (b.pest365Avg || 0) - (a.pest365Avg || 0));
  }, [data]);

  useEffect(() => {
    if (!sortedData || sortedData.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const height = sortedData.length * ROW_HEIGHT + MARGIN.top + MARGIN.bottom;
    const sideWidth = (width - MARGIN.left - MARGIN.right - CENTER_COL_WIDTH) / 2;

    svg.attr('viewBox', `0 0 ${width} ${height}`)
      .attr('width', width)
      .attr('height', height);

    const g = svg.append('g')
      .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Determine max price across both fields for symmetric scale
    const maxPrice = d3.max(sortedData, (d) =>
      Math.max(d.pest365Avg || 0, d.pest365PlusAvg || 0)
    );
    const xPadding = maxPrice * 0.1 || 5;

    // Left scale (pest365 - extends leftward from center)
    const xLeft = d3.scaleLinear()
      .domain([0, maxPrice + xPadding])
      .nice()
      .range([0, sideWidth]);

    // Right scale (pest365Plus - extends rightward from center)
    const xRight = d3.scaleLinear()
      .domain([0, maxPrice + xPadding])
      .nice()
      .range([0, sideWidth]);

    // Y scale
    const y = d3.scaleBand()
      .domain(sortedData.map((d) => d.state))
      .range([0, height - MARGIN.top - MARGIN.bottom])
      .padding(0.2);

    const leftOrigin = sideWidth;
    const rightOrigin = sideWidth + CENTER_COL_WIDTH;

    // Horizontal grid lines
    sortedData.forEach((d) => {
      const rowY = y(d.state) + y.bandwidth();
      g.append('line')
        .attr('x1', 0)
        .attr('x2', width - MARGIN.left - MARGIN.right)
        .attr('y1', rowY)
        .attr('y2', rowY)
        .attr('stroke', '#f3f4f6')
        .attr('stroke-width', 1);
    });

    // Left x-axis (top) - pest365 prices
    const leftAxisG = g.append('g')
      .attr('transform', `translate(0, -8)`);
    leftAxisG.call(
      d3.axisTop(
        d3.scaleLinear()
          .domain(xLeft.domain())
          .range([leftOrigin, 0])
      )
        .ticks(5)
        .tickSize(0)
        .tickFormat((d) => `$${d}`)
    )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '9px')
      .attr('fill', '#6b7280');

    // Right x-axis (top) - pest365Plus prices
    const rightAxisG = g.append('g')
      .attr('transform', `translate(${rightOrigin}, -8)`);
    rightAxisG.call(
      d3.axisTop(xRight)
        .ticks(5)
        .tickSize(0)
        .tickFormat((d) => `$${d}`)
    )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '9px')
      .attr('fill', '#6b7280');

    // Bottom axis labels
    const leftBottomG = g.append('g')
      .attr('transform', `translate(0, ${height - MARGIN.top - MARGIN.bottom + 8})`);
    leftBottomG.call(
      d3.axisBottom(
        d3.scaleLinear()
          .domain(xLeft.domain())
          .range([leftOrigin, 0])
      )
        .ticks(5)
        .tickSize(0)
        .tickFormat((d) => `$${d}`)
    )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '9px')
      .attr('fill', '#6b7280');

    const rightBottomG = g.append('g')
      .attr('transform', `translate(${rightOrigin}, ${height - MARGIN.top - MARGIN.bottom + 8})`);
    rightBottomG.call(
      d3.axisBottom(xRight)
        .ticks(5)
        .tickSize(0)
        .tickFormat((d) => `$${d}`)
    )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '9px')
      .attr('fill', '#6b7280');

    // Axis labels
    g.append('text')
      .attr('x', leftOrigin / 2)
      .attr('y', -25)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('font-weight', 600)
      .attr('fill', '#2563eb')
      .text('Pest365');

    g.append('text')
      .attr('x', rightOrigin + sideWidth / 2)
      .attr('y', -25)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('font-weight', 600)
      .attr('fill', '#7c3aed')
      .text('Pest365+');

    // Center divider line
    g.append('line')
      .attr('x1', leftOrigin + CENTER_COL_WIDTH / 2)
      .attr('x2', leftOrigin + CENTER_COL_WIDTH / 2)
      .attr('y1', -8)
      .attr('y2', height - MARGIN.top - MARGIN.bottom)
      .attr('stroke', '#e5e7eb')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3 3');

    // Draw bars and labels for each state
    sortedData.forEach((d) => {
      const barY = y(d.state);
      const barH = y.bandwidth();
      const midY = barY + barH / 2;
      const pest365Val = d.pest365Avg || 0;
      const pest365PlusVal = d.pest365PlusAvg || 0;
      const oneTimeVal = d.oneTimeAvg || 0;

      const rowGroup = g.append('g')
        .attr('class', 'pyramid-row')
        .style('cursor', 'pointer');

      // Invisible hit area for hover
      rowGroup.append('rect')
        .attr('x', 0)
        .attr('y', barY)
        .attr('width', width - MARGIN.left - MARGIN.right)
        .attr('height', barH)
        .attr('fill', 'transparent');

      // Left bar (pest365) - extends left from center
      const leftBarWidth = xLeft(pest365Val);
      rowGroup.append('rect')
        .attr('x', leftOrigin - leftBarWidth)
        .attr('y', barY)
        .attr('width', leftBarWidth)
        .attr('height', barH)
        .attr('fill', '#2563eb')
        .attr('opacity', 0.8)
        .attr('rx', 2);

      // Left value label
      rowGroup.append('text')
        .attr('x', leftOrigin - leftBarWidth - 4)
        .attr('y', midY)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'end')
        .attr('font-size', '9px')
        .attr('fill', '#2563eb')
        .attr('font-weight', 500)
        .text(`$${pest365Val.toFixed(0)}`);

      // Right bar (pest365Plus) - extends right from center
      const rightBarWidth = xRight(pest365PlusVal);
      rowGroup.append('rect')
        .attr('x', rightOrigin)
        .attr('y', barY)
        .attr('width', rightBarWidth)
        .attr('height', barH)
        .attr('fill', '#7c3aed')
        .attr('opacity', 0.8)
        .attr('rx', 2);

      // Right value label
      rowGroup.append('text')
        .attr('x', rightOrigin + rightBarWidth + 4)
        .attr('y', midY)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'start')
        .attr('font-size', '9px')
        .attr('fill', '#7c3aed')
        .attr('font-weight', 500)
        .text(`$${pest365PlusVal.toFixed(0)}`);

      // Center state label
      rowGroup.append('text')
        .attr('x', leftOrigin + CENTER_COL_WIDTH / 2)
        .attr('y', midY)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'middle')
        .attr('font-size', '11px')
        .attr('fill', '#374151')
        .attr('font-weight', 600)
        .text(d.state);

      // OneTime diamond marker on left bar side
      if (oneTimeVal > 0) {
        const diamondX = leftOrigin - xLeft(oneTimeVal);
        const diamondSize = 4;
        rowGroup.append('path')
          .attr('d', `M${diamondX},${midY - diamondSize} L${diamondX + diamondSize},${midY} L${diamondX},${midY + diamondSize} L${diamondX - diamondSize},${midY} Z`)
          .attr('fill', '#f59e0b')
          .attr('stroke', '#fff')
          .attr('stroke-width', 0.5);
      }

      // Hover interactions
      rowGroup
        .on('mouseenter', (event) => {
          rowGroup.selectAll('rect').filter(function () {
            return d3.select(this).attr('fill') !== 'transparent';
          }).attr('opacity', 1);

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            state: d.state,
            pest365Avg: pest365Val,
            pest365PlusAvg: pest365PlusVal,
            oneTimeAvg: oneTimeVal,
            addressCount: d.addressCount || 0,
          });
        })
        .on('mousemove', (event) => {
          setTooltip((prev) =>
            prev
              ? {
                  ...prev,
                  x: event.clientX + 12,
                  y: event.clientY - 10,
                }
              : prev
          );
        })
        .on('mouseleave', () => {
          rowGroup.selectAll('rect').filter(function () {
            return d3.select(this).attr('fill') !== 'transparent';
          }).attr('opacity', 0.8);

          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [sortedData]);

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>State Price Comparison (Pyramid)</div>
        <div style={styles.subtitle}>Pest365 vs Pest365+ average pricing by state</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>State Price Comparison (Pyramid)</div>
        <div style={styles.subtitle}>Pest365 vs Pest365+ average pricing by state</div>
      </div>
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendDot, background: '#2563eb' }} />
          <span>Pest365 (left)</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendDot, background: '#7c3aed' }} />
          <span>Pest365+ (right)</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendDiamond, background: '#f59e0b' }} />
          <span>One-Time Avg</span>
        </div>
      </div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.state}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#2563eb' }}>Pest365</span>
            <span style={{ fontWeight: 600 }}>${tooltip.pest365Avg.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#7c3aed' }}>Pest365+</span>
            <span style={{ fontWeight: 600 }}>${tooltip.pest365PlusAvg.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#f59e0b' }}>One-Time</span>
            <span style={{ fontWeight: 600 }}>${tooltip.oneTimeAvg.toFixed(2)}</span>
          </div>
          <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '4px', paddingTop: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>Addresses</span>
              <span style={{ fontWeight: 600 }}>{tooltip.addressCount}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
